import * as React from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage } from '@/components/ui/error-message';
import { PageCarousel, type CarouselPage } from '@/components/ui/page-carousel';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { Callout, KeyHint, WorkspaceButton } from '@/components/ui/workspace';
import { PdfPreviewModal } from '@/components/modals/pdf-preview-modal';
import type {
  EditorBlock,
  EditorCitation,
  RewriteRequest,
  RewriteResult,
  SaveBlockInput,
} from '@/components/report-editor';
import {
  newIdempotencyKey,
  rewriteReportBlock,
  useDraftVendorReport,
  usePublishVendorReport,
  usePublishedReportQuery,
  useReportBlockMutations,
  useVendorReportQuery,
  vendorReportKey,
  type ReportCitation,
  type ReportVersion,
} from '@/lib/api/vendor-intelligence-report';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { CitationDialog } from './citation-dialog';
import {
  errorReason,
  moduleNamer,
  plural,
  proseIssueLines,
  publishBlocker,
  saveError,
  toEditorCitation,
} from './report-logic';
import {
  carouselPages,
  modelFromPublished,
  modelFromReport,
  type ReportModel,
} from './report-model';
import { ReportPageView, type ReportPageEditing } from './report-page';
import { DRAFT_VIEW, VersionPicker, type ReportView } from './versions-list';

/**
 * Vendor intelligence → Report (T-0283; E24-design §5, §9; ADR-026
 * decisions 5 and 9): the report as the pages the PDF prints, moved
 * through sideways in the shared `PageCarousel`. Each page shows its code
 * tables and figures and its blocks in the shared editor (edit, rewrite,
 * back to the AI draft). Above the carousel: Prepare report (with an
 * optional focus line), Publish, the versions and the PDF. The strip's
 * dots come from the blocks' origins and its counts from the prose checks.
 * A published version opens read-only in the same carousel.
 *
 * Every number shown is the server's: figures are tokens the report fills,
 * tables are composed by code from the fit frozen at drafting.
 */
export function ReportStep({
  engagementId,
  ratingsReady,
  engagement,
  shortlistReady = true,
  className,
}: {
  engagementId: string;
  /** At least two vendors have confirmed ratings (the tab's own count). */
  ratingsReady: boolean;
  /** The engagement, for the cover's client line when a draft frozen under v1 carries none. */
  engagement?: { client_name: string; name: string } | null;
  /**
   * Every disposition is confirmed (the server's `dispositions_confirmed`,
   * T-0274; ADR-026 decision 3): the report's pages follow the shortlist,
   * so Prepare waits for it.
   */
  shortlistReady?: boolean;
  className?: string;
}) {
  const qc = useQueryClient();
  const query = useVendorReportQuery(engagementId);
  const report = query.data;
  const draftMutation = useDraftVendorReport(engagementId);
  const publish = usePublishVendorReport(engagementId);
  const { save, revert } = useReportBlockMutations(engagementId);
  const [focus, setFocus] = React.useState<string | null>(null);
  const [publishKey, setPublishKey] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [pdfVersion, setPdfVersion] = React.useState<ReportVersion | null>(null);
  const [openCitation, setOpenCitation] = React.useState<ReportCitation | null>(null);
  const [view, setView] = React.useState<ReportView>(DRAFT_VIEW);
  const [current, setCurrent] = React.useState<string | undefined>(undefined);
  // Citations a rewrite proposal brought in from the page's pool, by key.
  const proposed = React.useRef(new Map<string, ReportCitation>());

  const draft = report?.draft ?? null;
  const draftModel = React.useMemo(() => (report ? modelFromReport(report) : null), [report]);
  // Without a draft the latest published version is what there is to show.
  const effectiveView: ReportView =
    view.kind === 'draft' && !draft && report && report.versions.length > 0
      ? { kind: 'version', snapshotId: report.versions[0]!.snapshot_id }
      : view;
  const shownVersion =
    effectiveView.kind === 'version'
      ? (report?.versions.find((v) => v.snapshot_id === effectiveView.snapshotId) ?? null)
      : null;
  const published = usePublishedReportQuery(engagementId, shownVersion?.snapshot_id ?? null);
  const model: ReportModel | null = React.useMemo(() => {
    if (effectiveView.kind === 'draft') return draftModel;
    return published.data && shownVersion
      ? modelFromPublished(published.data, shownVersion.version)
      : null;
  }, [effectiveView.kind, draftModel, published.data, shownVersion]);
  const readOnly = effectiveView.kind !== 'draft' || !(report?.can_edit ?? false);
  const modelRef = React.useRef(model);
  modelRef.current = model;
  const focusValue = focus ?? draft?.focus ?? '';

  const pages: CarouselPage[] = React.useMemo(() => (model ? carouselPages(model) : []), [model]);
  const currentKey = current && pages.some((p) => p.key === current) ? current : pages[0]?.key;

  const onRewrite = React.useCallback(
    async (blockId: string, request: RewriteRequest): Promise<RewriteResult> => {
      const result = await rewriteReportBlock(engagementId, blockId, request);
      for (const c of result.proposal?.citations ?? []) proposed.current.set(c.key, c);
      const moduleName = moduleNamer(modelRef.current?.fit);
      return {
        proposal: result.proposal
          ? {
              text: result.proposal.text,
              citations: result.proposal.citations.map((c) => toEditorCitation(c, moduleName)),
            }
          : null,
        issues: result.issues,
        instruction: result.instruction,
      };
    },
    [engagementId],
  );

  const onSave = React.useCallback(
    async (blockId: string, input: SaveBlockInput) => {
      try {
        await save.mutateAsync({
          blockId,
          request: {
            text: input.text,
            origin: input.origin,
            ...(input.instruction ? { instruction: input.instruction } : {}),
            ...(input.citations ? { citation_keys: input.citations } : {}),
          },
        });
      } catch (err) {
        // A published report's block: read again, the new draft is the one to edit.
        if (errorReason(err) === 'report.published') {
          void qc.invalidateQueries({ queryKey: vendorReportKey(engagementId) });
        }
        throw saveError(err);
      }
    },
    [save, qc, engagementId],
  );

  const onRevert = React.useCallback(
    async (blockId: string) => {
      await revert.mutateAsync(blockId);
    },
    [revert],
  );

  const onOpenCitation = React.useCallback((c: EditorCitation, _block: EditorBlock) => {
    setOpenCitation(modelRef.current?.citations.get(c.id) ?? proposed.current.get(c.id) ?? null);
  }, []);

  const editing: ReportPageEditing = React.useMemo(
    () => ({ readOnly, onRewrite, onSave, onRevert, onOpenCitation }),
    [readOnly, onRewrite, onSave, onRevert, onOpenCitation],
  );

  const root = (children: React.ReactNode) => (
    <div
      data-testid="vi-report-step"
      className={cn('flex min-h-0 flex-1 flex-col gap-3 px-6 py-4', className)}
    >
      {children}
    </div>
  );

  if (!report) {
    return root(
      query.error ? (
        <ErrorMessage error={query.error} />
      ) : (
        <p className="m-0 flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner className="h-3.5 w-3.5" /> Loading the report…
        </p>
      ),
    );
  }

  const busy = draftMutation.isPending || publish.isPending;
  const blocked = report.draft_blocked;
  const issues = draftModel ? proseIssueLines(draftModel.pages) : [];
  const blocker = publishBlocker(report);
  // The tab's own gate before the server's: the pages follow the dispositions.
  const shortlistBlocker =
    !blocked && !shortlistReady
      ? 'Confirm the shortlist first. The report gives each shortlisted vendor a page and lists the alternates and set-aside vendors with your reasons.'
      : null;

  const runDraft = () => {
    setNotice(null);
    setPublishKey(null);
    setView(DRAFT_VIEW);
    const hadDraft = draft !== null;
    draftMutation.mutate(focusValue.trim() || null, {
      onSuccess: () =>
        setNotice(
          hadDraft
            ? 'Prepared again from the confirmed ratings. Paragraphs you edited or rewrote are kept.'
            : 'Drafted. Read it through page by page, edit or rewrite any paragraph, then publish.',
        ),
    });
  };

  const runPublish = () => {
    if (!publishKey) return;
    setNotice(null);
    publish.mutate(publishKey, {
      onSuccess: (out) => {
        setPublishKey(null);
        setNotice(
          `Published as version ${out.published.version}. The PDF is rendering; open it from the versions above. The next draft starts as a copy.`,
        );
      },
    });
  };

  const jumpTo = (blockId: string) => {
    const page = model?.pages.find((p) => p.blocks.some((b) => b.id === blockId));
    if (page) setCurrent(page.key);
    // The page must be current (and no longer inert) before its block can take focus.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-block-id="${blockId}"]`);
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      el?.focus();
    });
  };

  const prepare = report.can_edit ? (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor="vi-report-focus" className="sr-only">
        Focus for the report (optional)
      </label>
      <input
        id="vi-report-focus"
        value={focusValue}
        onChange={(e) => setFocus(e.target.value)}
        disabled={busy}
        maxLength={500}
        placeholder="Focus (optional), for example: Massachusetts statutory reporting"
        className="h-[30px] min-w-[200px] flex-1 rounded-card border border-ink-200 bg-paper-0 px-2.5 text-body-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-accent-amber-600 disabled:opacity-50"
      />
      <WorkspaceButton
        variant={draft ? 'secondary' : 'primary'}
        disabled={busy || blocked !== null || shortlistBlocker !== null}
        title={blocked?.message ?? shortlistBlocker ?? undefined}
        onClick={runDraft}
      >
        {draftMutation.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : draft ? (
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {draft ? 'Prepare again' : 'Prepare report'}
      </WorkspaceButton>
      {draft && report.can_publish ? (
        <WorkspaceButton
          variant="primary"
          disabled={busy || blocker !== null}
          title={blocker ?? undefined}
          onClick={() => setPublishKey(newIdempotencyKey())}
        >
          Publish
        </WorkspaceButton>
      ) : null}
      {model && !readOnly ? (
        <div className="ml-auto hidden flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-500 lg:flex">
          <KeyHint keys={['←', '→']}>page</KeyHint>
          <KeyHint keys={['j', 'k']}>move</KeyHint>
          <KeyHint keys={['e']}>edit</KeyHint>
          <KeyHint keys={['r']}>rewrite</KeyHint>
          <KeyHint keys={['Esc']}>close</KeyHint>
        </div>
      ) : null}
    </div>
  ) : null;

  const header = (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {draft ? (
          <>
            <StatusChip tone="amber" size="sm">
              Draft · version {draft.version}
            </StatusChip>
            <span className="text-meta text-ink-500">
              Drafted {formatDate(draft.drafted_at)}
              {draft.drafted_by.name ? ` by ${draft.drafted_by.name}` : ''}
              {draft.focus ? ` · focus: ${draft.focus}` : ''}
              {pages.length > 0 ? ` · ${plural(pages.length, 'page')}` : ''}
            </span>
          </>
        ) : null}
        {shownVersion ? (
          <StatusChip tone="frozen" size="sm">
            Version {shownVersion.version} · read-only
          </StatusChip>
        ) : null}
        <div className="ml-auto">
          <VersionPicker
            draftVersion={draft?.version ?? null}
            versions={report.versions}
            view={effectiveView}
            onView={(v) => {
              setView(v);
              setCurrent(undefined);
            }}
            onOpenPdf={setPdfVersion}
          />
        </div>
      </div>

      {!draft && blocked ? (
        <p className="m-0 rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm text-ink-500">
          {ratingsReady ? blocked.message : `Confirm ratings first. ${blocked.message}`}
        </p>
      ) : null}
      {shortlistBlocker ? (
        <p
          data-testid="vi-report-shortlist-blocker"
          className={cn(
            'm-0 text-ink-500',
            draft
              ? 'text-meta'
              : 'rounded-card border border-dashed border-ink-200 px-4 py-3 text-body-sm',
          )}
        >
          {shortlistBlocker}
        </p>
      ) : null}
      {!draft && !blocked && !shortlistBlocker ? (
        <p className="m-0 text-body-sm text-ink-700">
          {report.can_edit
            ? 'Prepare report drafts the pages from the confirmed ratings and the shortlist. The AI writes the words, each claim cited; every number is a figure computed by code. You then edit, rewrite or publish.'
            : 'A consultant prepares the report from the confirmed ratings.'}
        </p>
      ) : null}

      {prepare}
      {draft && blocker && draft.refusals === 0 ? (
        <p className="m-0 text-meta text-ink-500">{blocker}</p>
      ) : null}

      {draftMutation.isPending ? (
        <Callout tone="ai" icon={Sparkles} role="status">
          Drafting the report: one AI call per page and per shortlisted vendor. This takes a minute
          or two.
        </Callout>
      ) : null}
      {draftMutation.error ? <ErrorMessage error={draftMutation.error} /> : null}

      {draft && report.stale && effectiveView.kind === 'draft' ? (
        <Callout tone="warn" icon={AlertTriangle}>
          The confirmed ratings or the shortlist changed after this draft was prepared. Prepare it
          again to bring the figures up to date; paragraphs you edited or rewrote are kept.
        </Callout>
      ) : null}

      {draft && (draft.refusals > 0 || issues.length > 0) && effectiveView.kind === 'draft' ? (
        <Callout tone="warn" icon={AlertTriangle}>
          <div className="flex flex-col gap-1.5">
            {draft.refusals > 0 ? (
              <p className="m-0">
                {plural(draft.refusals, 'paragraph')} could not be drafted from cited evidence and{' '}
                {draft.refusals === 1 ? 'is' : 'are'} marked on{' '}
                {draft.refusals === 1 ? 'its' : 'their'} page. Write{' '}
                {draft.refusals === 1 ? 'it' : 'them'} before publishing.
              </p>
            ) : null}
            {issues.length > 0 ? (
              <div data-testid="report-prose-issues">
                <p className="m-0">
                  {issues.length === 1
                    ? 'One thing in the text is not a figure from the ratings.'
                    : `${issues.length} things in the text are not figures from the ratings.`}{' '}
                  Numbers belong in figures, which the report fills from code. You can still
                  publish.
                </p>
                <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0">
                  {issues.map((line, i) => (
                    <li key={`${line.blockId}-${i}`}>
                      <button
                        type="button"
                        onClick={() => jumpTo(line.blockId)}
                        className="text-left underline-offset-2 hover:underline"
                      >
                        <b className="font-semibold text-ink-900">{line.sectionTitle}</b>:{' '}
                        {line.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </Callout>
      ) : null}

      {publishKey && draft ? (
        <Callout tone="ask" icon={AlertTriangle}>
          Publishing freezes these {plural(pages.length, 'page')} as version {draft.version} and
          renders the PDF for the client, citing every record by its id. A published version never
          changes; the next draft starts as a copy.
          {issues.length > 0
            ? ` ${issues.length === 1 ? 'One number is' : `${issues.length} numbers are`} typed rather than figures.`
            : ''}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <WorkspaceButton variant="primary" disabled={publish.isPending} onClick={runPublish}>
              {publish.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
              Publish version {draft.version}
            </WorkspaceButton>
            <WorkspaceButton
              variant="tertiary"
              disabled={publish.isPending}
              onClick={() => setPublishKey(null)}
            >
              Cancel
            </WorkspaceButton>
          </div>
        </Callout>
      ) : null}
      {publish.error ? <ErrorMessage error={publish.error} /> : null}

      {notice ? (
        <Callout tone="ok" icon={CheckCircle2} role="status">
          {notice}
        </Callout>
      ) : null}

      {published.error && effectiveView.kind === 'version' ? (
        <ErrorMessage error={published.error} />
      ) : null}
    </div>
  );

  return root(
    <>
      {model && pages.length > 0 ? (
        <PageCarousel
          aria-label="Report pages"
          header={header}
          pages={pages}
          current={currentKey}
          onChange={(key) => setCurrent(key)}
          renderPage={(page, index) => {
            const pageModel = model.pages.find((p) => p.key === page.key);
            return pageModel ? (
              <ReportPageView
                model={model}
                page={pageModel}
                index={index}
                count={pages.length}
                editing={editing}
                engagement={engagement}
              />
            ) : null;
          }}
          className="min-h-0 flex-1"
        />
      ) : (
        <>
          {header}
          {effectiveView.kind === 'version' && published.isLoading ? (
            <p className="m-0 flex items-center gap-2 text-body-sm text-ink-500">
              <Spinner className="h-3.5 w-3.5" /> Loading version {shownVersion?.version}…
            </p>
          ) : null}
        </>
      )}

      <CitationDialog citation={openCitation} onClose={() => setOpenCitation(null)} />
      {pdfVersion ? (
        <PdfPreviewModal
          engagementId={engagementId}
          snapshotId={pdfVersion.snapshot_id}
          snapshotLabel={`Vendor intelligence report · version ${pdfVersion.version}`}
          open
          onOpenChange={(open) => {
            if (!open) setPdfVersion(null);
          }}
        />
      ) : null}
    </>,
  );
}
